import React from 'react';
import cx from '../cx';
import Inner from '../Inner';

/**
 * Legacy `tabs_block`, view only.
 *
 * One archive page uses it -- `/directives/directives_process`, five tabs
 * describing the directive processes. Its children are the richest nesting in
 * the archive: slate, html and image blocks, plus three `columnsBlock`
 * instances inside individual tabs.
 *
 * The stored `menu*` settings are presentation only and are mapped to classes.
 * The per-tab `selected` field is NOT a "this tab is active" flag: it holds a
 * BLOCK uid, left over from the previous site's editor, and three of the five
 * tabs carry one. It is ignored, and the first tab opens.
 *
 * Inactive panels stay in the DOM behind `hidden`, for the same reason the
 * accordion's do.
 */
const TabsView = ({ data = {}, ...rest }) => {
  const holder = data.data || {};
  const tabs = (holder.blocks_layout?.items || [])
    .map((uid) => [uid, holder.blocks?.[uid]])
    .filter(([, tab]) => tab);

  const [active, setActive] = React.useState(0);
  const baseId = React.useId();
  const refs = React.useRef([]);

  if (tabs.length === 0) return null;

  const focusTab = (index) => {
    setActive(index);
    refs.current[index]?.focus();
  };

  const onKeyDown = (event) => {
    const last = tabs.length - 1;
    const moves = {
      ArrowRight: active === last ? 0 : active + 1,
      ArrowLeft: active === 0 ? last : active - 1,
      Home: 0,
      End: last,
    };
    if (!(event.key in moves)) return;
    event.preventDefault();
    focusTab(moves[event.key]);
  };

  return (
    <div
      className={cx('archives-tabs', {
        'archives-tabs--pointing': data.menuPointing,
        'archives-tabs--secondary': data.menuSecondary,
        'archives-tabs--fluid': data.menuFluid,
        'archives-tabs--compact': data.menuCompact,
        'archives-tabs--borderless': data.menuBorderless,
        [`archives-tabs--${data.menuSize}`]: data.menuSize,
        [`archives-tabs--color-${data.menuColor}`]: data.menuColor,
      })}
      style={
        data.verticalAlign
          ? { '--archives-tabs-align': data.verticalAlign }
          : undefined
      }
    >
      <div role="tablist" className="archives-tabs__menu" onKeyDown={onKeyDown}>
        {tabs.map(([uid, tab], index) => (
          <button
            key={uid}
            type="button"
            role="tab"
            id={`${baseId}-t-${index}`}
            ref={(node) => {
              refs.current[index] = node;
            }}
            className={cx('archives-tabs__tab', {
              'is-active': index === active,
            })}
            aria-selected={index === active}
            aria-controls={`${baseId}-p-${index}`}
            tabIndex={index === active ? 0 : -1}
            onClick={() => setActive(index)}
          >
            {tab.title}
          </button>
        ))}
      </div>
      {tabs.map(([uid, tab], index) => (
        <div
          key={uid}
          role="tabpanel"
          id={`${baseId}-p-${index}`}
          aria-labelledby={`${baseId}-t-${index}`}
          className="archives-tabs__panel"
          hidden={index !== active}
          tabIndex={0}
        >
          <Inner holder={tab} {...rest} />
        </div>
      ))}
    </div>
  );
};

export default TabsView;
